import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { Loader2, Search, Users, User, Shield, Building, Eye, X, UserPlus, LogIn, Phone, Mail, Chrome, MessageSquare, Smartphone } from 'lucide-react';
import { clearAuthenticaCache } from '@/lib/services/authentica';
import type { OTPMethod } from '@/lib/services/authentica';
import {
  getMessagingProviderStatus,
  keyStatusText,
  type MessagingProviderStatus,
} from '@/lib/services/messagingProviderStatus';
import {
  CustomerPermissions,
  CustomerDashboardSection,
  CustomerPermissionAction,
  CUSTOMER_SECTION_LABELS,
  CUSTOMER_ACTION_LABELS,
  GlobalCustomerPermissions,
  DEFAULT_CUSTOMER_PERMISSIONS,
} from '@/types/permissions';
import {
  getGlobalCustomerPermissions,
  updateGlobalCustomerPermissions,
  setUserPermissionOverride,
  removeUserPermissionOverride,
  setOrganizationPermissionOverride,
  removeOrganizationPermissionOverride,
} from '@/lib/firebase/queries/permissions';
import { getAllUsers } from '@/lib/firebase/queries/users';
import { getOrganizations } from '@/lib/firebase/queries/organizations';
import { useLanguage } from '@/contexts/LanguageContext';
import { hasRole } from '@/lib/utils/roles';

interface UserSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface UserType {
  id: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  email?: string;
  phone?: string;
}

interface OrganizationType {
  id: string;
  name: string;
  type: string;
}

export function UserSettingsDialog({ open, onOpenChange }: UserSettingsDialogProps) {
  const { currentUser } = useAuth();
  const { t, dir, language } = useLanguage();
  const isRTL = language === 'ar';
  
  // Main tab state
  const [mainTab, setMainTab] = useState('permissions');
  
  // Permissions state
  const [loading, setLoading] = useState(false);
  const [permissionsTab, setPermissionsTab] = useState('global');
  const [globalPermissions, setGlobalPermissions] = useState<GlobalCustomerPermissions | null>(null);
  const [globalDraft, setGlobalDraft] = useState<CustomerPermissions>(DEFAULT_CUSTOMER_PERMISSIONS);
  const [users, setUsers] = useState<UserType[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [userDraft, setUserDraft] = useState<CustomerPermissions>(DEFAULT_CUSTOMER_PERMISSIONS);
  const [hasUserOverride, setHasUserOverride] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationType[]>([]);
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<OrganizationType | null>(null);
  const [organizationDraft, setOrganizationDraft] = useState<CustomerPermissions>(DEFAULT_CUSTOMER_PERMISSIONS);
  const [hasOrganizationOverride, setHasOrganizationOverride] = useState(false);

  // Signup settings state
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [signupLoading, setSignupLoading] = useState(false);
  
  // Login methods state
  const [loginMethods, setLoginMethods] = useState({
    phoneOtp: true,
    emailPassword: true,
    googleLogin: true,
  });
  const [loginMethodsLoading, setLoginMethodsLoading] = useState(false);

  // Mobile login methods state (separate doc from web)
  const [mobileLoginMethods, setMobileLoginMethods] = useState({
    phoneOtp: true,
    emailPassword: true,
  });
  // Per-platform overrides stored under `ios` / `android` in the same doc. When a
  // platform has no block the app falls back to the flags above, so these start
  // out mirroring them and only become explicit once an admin toggles one.
  const [mobilePlatformMethods, setMobilePlatformMethods] = useState({
    ios: { phoneOtp: true, emailPassword: true },
    android: { phoneOtp: true, emailPassword: true },
  });
  const [mobileLoginMethodsLoading, setMobileLoginMethodsLoading] = useState(false);

  // Authentica OTP settings state. The API keys themselves are server secrets
  // (namat#130): this screen only shows whether they are configured.
  const [otpMethod, setOtpMethodState] = useState<OTPMethod>('whatsapp');
  const [providerStatus, setProviderStatus] = useState<MessagingProviderStatus | null>(null);

  // Evolution API settings state (URL + instance only; the key is a secret)
  const [evolutionApiUrl, setEvolutionApiUrl] = useState('');
  const [evolutionInstanceName, setEvolutionInstanceName] = useState('');
  const [evolutionLoading, setEvolutionLoading] = useState(false);

  // Load data when dialog opens
  useEffect(() => {
    if (open) {
      loadData();
      fetchSignupStatus();
      fetchLoginMethods();
      fetchMobileLoginMethods();
      fetchAuthenticaSettings();
      fetchEvolutionSettings();
      getMessagingProviderStatus().then(setProviderStatus);
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [globalPerms, allUsers, allOrganizations] = await Promise.all([
        getGlobalCustomerPermissions(),
        getAllUsers(),
        getOrganizations(),
      ]);

      setGlobalPermissions(globalPerms);
      setGlobalDraft(globalPerms.defaultPermissions);
      
      const customers = allUsers.filter(user => hasRole(user.roles, 'customer'));
      setUsers(customers);
      setOrganizations(allOrganizations);
    } catch (error) {
      console.error('Error loading permissions data:', error);
      toast.error(isRTL ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSignupStatus = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'globalSignup'));
      if (settingsDoc.exists()) {
        setSignupEnabled(settingsDoc.data().enabled !== false);
      }
    } catch (error) {
      console.error('Error loading signup status:', error);
    }
  };

  const fetchLoginMethods = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'loginMethods'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setLoginMethods({
          phoneOtp: data.phoneOtp !== false,
          emailPassword: data.emailPassword !== false,
          googleLogin: data.googleLogin !== false,
        });
      }
    } catch (error) {
      console.error('Error loading login methods:', error);
    }
  };

  const fetchMobileLoginMethods = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'loginMethodsMobile'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        const base = {
          phoneOtp: data.phoneOtp !== false,
          emailPassword: data.emailPassword !== false,
        };
        setMobileLoginMethods(base);
        // A missing platform block, or a missing key inside one, inherits the
        // base flag — the same rule the app applies in resolveLoginMethods.
        const forPlatform = (block: any) => ({
          phoneOtp: block && 'phoneOtp' in block ? block.phoneOtp !== false : base.phoneOtp,
          emailPassword:
            block && 'emailPassword' in block ? block.emailPassword !== false : base.emailPassword,
        });
        setMobilePlatformMethods({
          ios: forPlatform(data.ios),
          android: forPlatform(data.android),
        });
      }
    } catch (error) {
      console.error('Error loading mobile login methods:', error);
    }
  };

  const fetchAuthenticaSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'authenticaApi'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setOtpMethodState((data.otpMethod as OTPMethod) || 'whatsapp');
      }
    } catch (error) {
      console.error('Error loading Authentica settings:', error);
    }
  };

  const handleOtpMethodChange = async (method: OTPMethod) => {
    setOtpMethodState(method);
    try {
      // merge, not read-spread-write: never copies the legacy apiKey field
      // back into the document (namat#130).
      await setDoc(doc(db, 'settings', 'authenticaApi'), {
        otpMethod: method,
        updatedAt: new Date()
      }, { merge: true });
      clearAuthenticaCache();
      toast.success(isRTL
        ? (method === 'sms' ? 'تم التغيير إلى SMS' : 'تم التغيير إلى واتساب')
        : (method === 'sms' ? 'Switched to SMS' : 'Switched to WhatsApp')
      );
    } catch (error) {
      console.error('Error saving OTP method:', error);
      toast.error(isRTL ? 'فشل حفظ طريقة التحقق' : 'Failed to save OTP method');
    }
  };

  const fetchEvolutionSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'evolutionApi'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setEvolutionApiUrl(data.apiUrl || '');
        setEvolutionInstanceName(data.instanceName || '');
      }
    } catch (error) {
      console.error('Error loading Evolution API settings:', error);
    }
  };

  const handleSaveEvolutionSettings = async () => {
    try {
      setEvolutionLoading(true);
      // merge: the key is no longer written from here, and a plain setDoc
      // would drop the legacy apiKey the server still falls back to until the
      // secret is set (namat#130).
      await setDoc(doc(db, 'settings', 'evolutionApi'), {
        apiUrl: evolutionApiUrl,
        instanceName: evolutionInstanceName,
        updatedAt: new Date()
      }, { merge: true });
      getMessagingProviderStatus().then(setProviderStatus);
      toast.success(isRTL ? 'تم حفظ إعدادات Evolution API' : 'Evolution API settings saved');
    } catch (error) {
      console.error('Error saving Evolution API settings:', error);
      toast.error(isRTL ? 'فشل حفظ إعدادات Evolution API' : 'Failed to save Evolution API settings');
    } finally {
      setEvolutionLoading(false);
    }
  };

  // Signup toggle handler
  const handleSignupToggle = async (checked: boolean) => {
    try {
      setSignupLoading(true);
      await setDoc(doc(db, 'settings', 'globalSignup'), {
        enabled: checked,
        updatedAt: new Date()
      });
      setSignupEnabled(checked);
      toast.success(checked 
        ? (isRTL ? 'تم تفعيل التسجيل العام' : 'Global signup enabled')
        : (isRTL ? 'تم تعطيل التسجيل العام' : 'Global signup disabled')
      );
    } catch (error) {
      console.error('Error toggling signup:', error);
      toast.error(isRTL ? 'فشل تحديث حالة التسجيل' : 'Failed to update signup status');
    } finally {
      setSignupLoading(false);
    }
  };

  // Login method toggle handler
  const handleLoginMethodToggle = async (method: keyof typeof loginMethods, checked: boolean) => {
    const newMethods = { ...loginMethods, [method]: checked };
    const enabledMethods = Object.values(newMethods).filter(Boolean).length;
    
    if (enabledMethods === 0) {
      toast.error(isRTL ? 'يجب تفعيل طريقة تسجيل دخول واحدة على الأقل' : 'At least one login method must be enabled');
      return;
    }

    try {
      setLoginMethodsLoading(true);
      setLoginMethods(newMethods);
      await setDoc(doc(db, 'settings', 'loginMethods'), {
        ...newMethods,
        updatedAt: new Date()
      });
      toast.success(isRTL ? 'تم تحديث طرق تسجيل الدخول' : 'Login methods updated');
    } catch (error) {
      console.error('Error updating login methods:', error);
      toast.error(isRTL ? 'فشل تحديث طرق تسجيل الدخول' : 'Failed to update login methods');
      setLoginMethods(loginMethods);
    } finally {
      setLoginMethodsLoading(false);
    }
  };

  // Mobile login method toggle handler (separate doc)
  const handleMobileLoginMethodToggle = async (method: keyof typeof mobileLoginMethods, checked: boolean) => {
    const newMethods = { ...mobileLoginMethods, [method]: checked };
    const enabledMethods = Object.values(newMethods).filter(Boolean).length;

    if (enabledMethods === 0) {
      toast.error(isRTL ? 'يجب تفعيل طريقة تسجيل دخول واحدة على الأقل' : 'At least one login method must be enabled');
      return;
    }

    try {
      setMobileLoginMethodsLoading(true);
      setMobileLoginMethods(newMethods);
      // merge: the doc also carries `ios` / `android` blocks that a plain
      // setDoc would silently delete.
      await setDoc(doc(db, 'settings', 'loginMethodsMobile'), {
        ...newMethods,
        updatedAt: new Date()
      }, { merge: true });
      toast.success(isRTL ? 'تم تحديث طرق تسجيل الدخول للجوال' : 'Mobile login methods updated');
    } catch (error) {
      console.error('Error updating mobile login methods:', error);
      toast.error(isRTL ? 'فشل تحديث طرق تسجيل الدخول للجوال' : 'Failed to update mobile login methods');
      setMobileLoginMethods(mobileLoginMethods);
    } finally {
      setMobileLoginMethodsLoading(false);
    }
  };

  // Per-platform override. Writing one always writes the platform's full pair,
  // so a block is never half-specified and the app's per-key fallback only ever
  // applies to platforms an admin has not touched.
  const handleMobilePlatformToggle = async (
    platform: 'ios' | 'android',
    method: 'phoneOtp' | 'emailPassword',
    checked: boolean
  ) => {
    const newPlatform = { ...mobilePlatformMethods[platform], [method]: checked };

    if (!newPlatform.phoneOtp && !newPlatform.emailPassword) {
      toast.error(isRTL ? 'يجب تفعيل طريقة تسجيل دخول واحدة على الأقل' : 'At least one login method must be enabled');
      return;
    }

    const previous = mobilePlatformMethods;
    try {
      setMobileLoginMethodsLoading(true);
      setMobilePlatformMethods({ ...previous, [platform]: newPlatform });
      await setDoc(doc(db, 'settings', 'loginMethodsMobile'), {
        [platform]: newPlatform,
        updatedAt: new Date()
      }, { merge: true });
      toast.success(isRTL ? 'تم تحديث طرق تسجيل الدخول للجوال' : 'Mobile login methods updated');
    } catch (error) {
      console.error('Error updating mobile platform login methods:', error);
      toast.error(isRTL ? 'فشل تحديث طرق تسجيل الدخول للجوال' : 'Failed to update mobile login methods');
      setMobilePlatformMethods(previous);
    } finally {
      setMobileLoginMethodsLoading(false);
    }
  };

  // Permission handlers
  const handleUserSelect = (user: UserType) => {
    setSelectedUser(user);
    if (!globalPermissions) return;
    
    const userOverride = globalPermissions.userOverrides.find(override => override.userId === user.id);
    if (userOverride) {
      setUserDraft(userOverride.permissions);
      setHasUserOverride(true);
    } else {
      setUserDraft(globalPermissions.defaultPermissions);
      setHasUserOverride(false);
    }
  };

  const handleOrganizationSelect = (organization: OrganizationType) => {
    setSelectedOrganization(organization);
    if (!globalPermissions) return;
    
    const organizationOverride = globalPermissions.organizationOverrides.find(
      override => override.organizationId === organization.id
    );
    if (organizationOverride) {
      setOrganizationDraft(organizationOverride.permissions);
      setHasOrganizationOverride(true);
    } else {
      setOrganizationDraft(globalPermissions.defaultPermissions);
      setHasOrganizationOverride(false);
    }
  };

  const handleSaveGlobal = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      await updateGlobalCustomerPermissions(globalDraft, currentUser.uid);
      await loadData();
      toast.success(isRTL ? 'تم حفظ الإعدادات العامة' : 'Global settings saved');
    } catch (error) {
      console.error('Error saving global permissions:', error);
      toast.error(isRTL ? 'فشل حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveUser = async () => {
    if (!currentUser || !selectedUser) return;
    setLoading(true);
    try {
      if (hasUserOverride) {
        await setUserPermissionOverride(selectedUser.id, userDraft, currentUser.uid);
      } else {
        await removeUserPermissionOverride(selectedUser.id, currentUser.uid);
      }
      await loadData();
      toast.success(isRTL ? 'تم حفظ إعدادات المستخدم' : 'User settings saved');
    } catch (error) {
      console.error('Error saving user permissions:', error);
      toast.error(isRTL ? 'فشل حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUserOverride = async () => {
    if (!currentUser || !selectedUser) return;
    setLoading(true);
    try {
      await removeUserPermissionOverride(selectedUser.id, currentUser.uid);
      setHasUserOverride(false);
      setUserDraft(globalPermissions?.defaultPermissions || DEFAULT_CUSTOMER_PERMISSIONS);
      await loadData();
      toast.success(isRTL ? 'تم إزالة التخصيص' : 'Override removed');
    } catch (error) {
      console.error('Error removing user override:', error);
      toast.error(isRTL ? 'فشل إزالة التخصيص' : 'Failed to remove override');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganization = async () => {
    if (!currentUser || !selectedOrganization) return;
    setLoading(true);
    try {
      if (hasOrganizationOverride) {
        await setOrganizationPermissionOverride(
          selectedOrganization.id, 
          selectedOrganization.name, 
          organizationDraft, 
          currentUser.uid
        );
      } else {
        await removeOrganizationPermissionOverride(selectedOrganization.id, currentUser.uid);
      }
      await loadData();
      toast.success(isRTL ? 'تم حفظ إعدادات المنظمة' : 'Organization settings saved');
    } catch (error) {
      console.error('Error saving organization permissions:', error);
      toast.error(isRTL ? 'فشل حفظ الإعدادات' : 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOrganizationOverride = async () => {
    if (!currentUser || !selectedOrganization) return;
    setLoading(true);
    try {
      await removeOrganizationPermissionOverride(selectedOrganization.id, currentUser.uid);
      setHasOrganizationOverride(false);
      setOrganizationDraft(globalPermissions?.defaultPermissions || DEFAULT_CUSTOMER_PERMISSIONS);
      await loadData();
      toast.success(isRTL ? 'تم إزالة تخصيص المنظمة' : 'Organization override removed');
    } catch (error) {
      console.error('Error removing organization override:', error);
      toast.error(isRTL ? 'فشل إزالة التخصيص' : 'Failed to remove override');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    switch (permissionsTab) {
      case 'global':
        await handleSaveGlobal();
        break;
      case 'user':
        if (selectedUser && hasUserOverride) {
          await handleSaveUser();
        }
        break;
      case 'organization':
        if (selectedOrganization && hasOrganizationOverride) {
          await handleSaveOrganization();
        }
        break;
    }
  };

  const isSaveEnabled = () => {
    switch (permissionsTab) {
      case 'global':
        return true;
      case 'user':
        return selectedUser && hasUserOverride;
      case 'organization':
        return selectedOrganization && hasOrganizationOverride;
      default:
        return false;
    }
  };

  // Explains why the Save button is disabled, so the toggle-gating never
  // silently no-ops on the admin — returns null when Save is enabled.
  const getSaveDisabledHint = () => {
    if (permissionsTab === 'user') {
      if (!selectedUser) {
        return isRTL ? 'اختر مستخدماً أولاً' : 'Select a user first';
      }
      if (!hasUserOverride) {
        return isRTL
          ? 'فعّل "تخصيص صلاحيات هذا المستخدم" أعلاه لحفظ التغييرات'
          : 'Turn on "Customize permissions for this user" above to save changes';
      }
    }
    if (permissionsTab === 'organization') {
      if (!selectedOrganization) {
        return isRTL ? 'اختر منظمة أولاً' : 'Select an organization first';
      }
      if (!hasOrganizationOverride) {
        return isRTL
          ? 'فعّل "تخصيص صلاحيات هذه المنظمة" أعلاه لحفظ التغييرات'
          : 'Turn on "Customize permissions for this organization" above to save changes';
      }
    }
    return null;
  };

  const filteredUsers = users.filter(user => {
    const search = userSearch.toLowerCase();
    const fullNameEn = `${user.firstNameEn || ''} ${user.lastNameEn || ''}`.toLowerCase();
    const fullNameAr = `${user.firstNameAr || ''} ${user.lastNameAr || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    const phone = (user.phone || '').toLowerCase();
    return (
      fullNameEn.includes(search) ||
      fullNameAr.includes(search) ||
      email.includes(search) ||
      phone.includes(search)
    );
  });

  const filteredOrganizations = organizations.filter(organization =>
    organization.name.toLowerCase().includes(organizationSearch.toLowerCase())
  );

  const renderPermissionSection = (
    permissions: CustomerPermissions,
    setPermissions: (permissions: CustomerPermissions) => void,
    disabled = false
  ) => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <Eye className="h-5 w-5" />
            {isRTL ? 'أقسام لوحة التحكم' : 'Dashboard Sections'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(Object.keys(CUSTOMER_SECTION_LABELS) as CustomerDashboardSection[]).map((section) => (
            <div key={section} className="flex items-center justify-between" dir={dir}>
              <Label htmlFor={`section-${section}`} className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {isRTL ? CUSTOMER_SECTION_LABELS[section].ar : CUSTOMER_SECTION_LABELS[section].en}
              </Label>
              <Switch
                id={`section-${section}`}
                checked={permissions.sections[section]}
                onCheckedChange={(checked) => {
                  setPermissions({
                    ...permissions,
                    sections: { ...permissions.sections, [section]: checked },
                  });
                }}
                disabled={disabled}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
            <Shield className="h-5 w-5" />
            {isRTL ? 'صلاحيات الإجراءات' : 'Action Permissions'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(Object.keys(permissions.actions) as CustomerPermissionAction[]).map((action) => (
            <div key={action} className="flex items-center justify-between" dir={dir}>
              <Label htmlFor={`action-${action}`} className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
                {isRTL ? CUSTOMER_ACTION_LABELS[action].ar : CUSTOMER_ACTION_LABELS[action].en}
              </Label>
              <Switch
                id={`action-${action}`}
                checked={permissions.actions[action]}
                onCheckedChange={(checked) => {
                  setPermissions({
                    ...permissions,
                    actions: { ...permissions.actions, [action]: checked },
                  });
                }}
                disabled={disabled}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-6xl h-[85vh] flex flex-col" 
        hideCloseButton
        dir={dir}
      >
        <DialogHeader className={`flex-shrink-0 flex ${isRTL ? 'flex-row' : 'flex-row-reverse'} items-center justify-between`}>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {isRTL ? 'إعدادات المستخدمين' : 'Users Settings'}
          </DialogTitle>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" className="h-6 w-6">
              <X className="h-4 w-4" />
            </Button>
          </DialogClose>
        </DialogHeader>

        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="flex-1 flex flex-col overflow-hidden" dir={dir}>
          <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              {isRTL ? 'الصلاحيات' : 'Permissions'}
            </TabsTrigger>
            <TabsTrigger value="signup" className="flex items-center gap-2">
              <UserPlus className="h-4 w-4" />
              {isRTL ? 'إعدادات التسجيل' : 'Signup Settings'}
            </TabsTrigger>
            <TabsTrigger value="login" className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              {isRTL ? 'طرق تسجيل الدخول' : 'Login Methods'}
            </TabsTrigger>
            <TabsTrigger value="otp" className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" />
              {isRTL ? 'طرق التحقق' : 'OTP Settings'}
            </TabsTrigger>
          </TabsList>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="flex-1 overflow-hidden mt-4">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col h-full min-h-0">
                {/* flex-1 min-h-0, NOT h-full: h-full made this consume the whole
                    column, pushing the Save button below it. The parent
                    TabsContent is overflow-hidden, so the button was clipped
                    with no scrollbar — every permission change looked like it
                    silently failed to persist, because it was never submitted. */}
                <Tabs value={permissionsTab} onValueChange={setPermissionsTab} className="flex flex-col flex-1 min-h-0">
                  <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
                    <TabsTrigger value="global" className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {isRTL ? 'الإعدادات العامة' : 'Global Settings'}
                    </TabsTrigger>
                    <TabsTrigger value="organization" className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {isRTL ? 'المنظمات' : 'Organizations'}
                    </TabsTrigger>
                    <TabsTrigger value="user" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {isRTL ? 'المستخدمين' : 'Users'}
                    </TabsTrigger>
                  </TabsList>

                  {/* Global Permissions */}
                  <TabsContent value="global" className="flex-1 overflow-hidden">
                    <div className="h-full flex flex-col">
                      <div className={`mb-4 flex-shrink-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <h3 className="text-lg font-semibold">
                          {isRTL ? 'الصلاحيات الافتراضية' : 'Default Permissions'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {isRTL ? 'الصلاحيات الافتراضية لجميع المستخدمين' : 'Default permissions for all customers'}
                        </p>
                      </div>
                      <ScrollArea className="flex-1">
                        <div className={isRTL ? 'pl-4' : 'pr-4'}>
                          {renderPermissionSection(globalDraft, setGlobalDraft)}
                        </div>
                      </ScrollArea>
                    </div>
                  </TabsContent>

                  {/* User Permissions */}
                  <TabsContent value="user" className="flex-1 overflow-hidden">
                    <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
                      <Card className="md:col-span-1 flex flex-col overflow-hidden">
                        <CardHeader className="pb-4 flex-shrink-0">
                          <CardTitle className="text-base">
                            {isRTL ? 'اختر مستخدم' : 'Select User'}
                          </CardTitle>
                          <div className="relative">
                            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                            <Input
                              placeholder={isRTL ? 'بحث...' : 'Search...'}
                              value={userSearch}
                              onChange={(e) => setUserSearch(e.target.value)}
                              className={isRTL ? 'pr-10' : 'pl-10'}
                              dir={dir}
                            />
                          </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 overflow-hidden">
                          <ScrollArea className="h-full">
                            <div className="space-y-1 p-4">
                              {filteredUsers.map((user) => (
                                <div
                                  key={user.id}
                                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                                    selectedUser?.id === user.id
                                      ? 'bg-primary/10 border-primary'
                                      : 'hover:bg-muted border-transparent'
                                  }`}
                                  onClick={() => handleUserSelect(user)}
                                >
                                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div className={isRTL ? 'text-right' : 'text-left'}>
                                      <p className="font-medium">
                                        {isRTL
                                          ? `${user.firstNameAr} ${user.lastNameAr}`
                                          : `${user.firstNameEn} ${user.lastNameEn}`}
                                      </p>
                                      <p className="text-sm text-muted-foreground">
                                        {user.email || user.phone || (isRTL ? 'لا يوجد بريد إلكتروني' : 'No email')}
                                      </p>
                                    </div>
                                    {globalPermissions?.userOverrides.some(o => o.userId === user.id) && (
                                      <Badge variant="secondary" className="text-xs">
                                        {isRTL ? 'مخصص' : 'Custom'}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>

                      <Card className="md:col-span-2 flex flex-col overflow-hidden">
                        <CardHeader className="pb-4 flex-shrink-0">
                          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <CardTitle className={`text-base ${isRTL ? 'text-right' : 'text-left'}`}>
                              {selectedUser
                                ? (isRTL
                                    ? `${selectedUser.firstNameAr} ${selectedUser.lastNameAr}`
                                    : `${selectedUser.firstNameEn} ${selectedUser.lastNameEn}`)
                                : (isRTL ? 'لم يتم اختيار مستخدم' : 'No user selected')
                              }
                            </CardTitle>
                            {selectedUser && (
                              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Switch id="user-customize" checked={hasUserOverride} onCheckedChange={setHasUserOverride} />
                                <Label htmlFor="user-customize">
                                  {isRTL ? 'تخصيص صلاحيات هذا المستخدم' : 'Customize permissions for this user'}
                                </Label>
                                {hasUserOverride && (
                                  <Button onClick={handleRemoveUserOverride} variant="outline" size="sm" disabled={loading}>
                                    {isRTL ? 'إعادة للافتراضي' : 'Reset'}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          {selectedUser && !hasUserOverride && (
                            <p className={`text-xs text-muted-foreground mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {isRTL
                                ? 'يتبع هذا المستخدم حالياً الصلاحيات الافتراضية العامة. المفاتيح أدناه للعرض فقط حتى يتم تفعيل التخصيص أعلاه.'
                                : 'This user currently inherits the global default permissions. The toggles below are read-only until you turn on customization above.'}
                            </p>
                          )}
                        </CardHeader>
                        <CardContent className="flex-1 overflow-hidden">
                          {selectedUser ? (
                            <ScrollArea className="h-full">
                              <div className={isRTL ? 'pl-4' : 'pr-4'}>
                                {renderPermissionSection(userDraft, setUserDraft, !hasUserOverride)}
                              </div>
                            </ScrollArea>
                          ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                              {isRTL ? 'اختر مستخدم للتعديل' : 'Select a user to edit'}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  {/* Organization Permissions */}
                  <TabsContent value="organization" className="flex-1 overflow-hidden">
                    <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden">
                      <Card className="md:col-span-1 flex flex-col overflow-hidden">
                        <CardHeader className="pb-4 flex-shrink-0">
                          <CardTitle className="text-base">
                            {isRTL ? 'اختر منظمة' : 'Select Organization'}
                          </CardTitle>
                          <div className="relative">
                            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground`} />
                            <Input
                              placeholder={isRTL ? 'بحث...' : 'Search...'}
                              value={organizationSearch}
                              onChange={(e) => setOrganizationSearch(e.target.value)}
                              className={isRTL ? 'pr-10' : 'pl-10'}
                              dir={dir}
                            />
                          </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 overflow-hidden">
                          <ScrollArea className="h-full">
                            <div className="space-y-1 p-4">
                              {filteredOrganizations.map((org) => (
                                <div
                                  key={org.id}
                                  className={`p-3 rounded-lg cursor-pointer border transition-colors ${
                                    selectedOrganization?.id === org.id
                                      ? 'bg-primary/10 border-primary'
                                      : 'hover:bg-muted border-transparent'
                                  }`}
                                  onClick={() => handleOrganizationSelect(org)}
                                >
                                  <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    <div className={isRTL ? 'text-right' : 'text-left'}>
                                      <p className="font-medium">{org.name}</p>
                                      <p className="text-sm text-muted-foreground">{org.type}</p>
                                    </div>
                                    {globalPermissions?.organizationOverrides.some(o => o.organizationId === org.id) && (
                                      <Badge variant="secondary" className="text-xs">
                                        {isRTL ? 'مخصص' : 'Custom'}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </CardContent>
                      </Card>

                      <Card className="md:col-span-2 flex flex-col overflow-hidden">
                        <CardHeader className="pb-4 flex-shrink-0">
                          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <CardTitle className={`text-base ${isRTL ? 'text-right' : 'text-left'}`}>
                              {selectedOrganization?.name || (isRTL ? 'لم يتم اختيار منظمة' : 'No organization selected')}
                            </CardTitle>
                            {selectedOrganization && (
                              <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <Switch id="organization-customize" checked={hasOrganizationOverride} onCheckedChange={setHasOrganizationOverride} />
                                <Label htmlFor="organization-customize">
                                  {isRTL ? 'تخصيص صلاحيات هذه المنظمة' : 'Customize permissions for this organization'}
                                </Label>
                                {hasOrganizationOverride && (
                                  <Button onClick={handleRemoveOrganizationOverride} variant="outline" size="sm" disabled={loading}>
                                    {isRTL ? 'إعادة للافتراضي' : 'Reset'}
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                          {selectedOrganization && !hasOrganizationOverride && (
                            <p className={`text-xs text-muted-foreground mt-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                              {isRTL
                                ? 'تتبع هذه المنظمة حالياً الصلاحيات الافتراضية العامة. المفاتيح أدناه للعرض فقط حتى يتم تفعيل التخصيص أعلاه.'
                                : 'This organization currently inherits the global default permissions. The toggles below are read-only until you turn on customization above.'}
                            </p>
                          )}
                        </CardHeader>
                        <CardContent className="flex-1 overflow-hidden">
                          {selectedOrganization ? (
                            <ScrollArea className="h-full">
                              <div className={isRTL ? 'pl-4' : 'pr-4'}>
                                {renderPermissionSection(organizationDraft, setOrganizationDraft, !hasOrganizationOverride)}
                              </div>
                            </ScrollArea>
                          ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                              {isRTL ? 'اختر منظمة للتعديل' : 'Select an organization to edit'}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>
                </Tabs>

                {/* Save button for permissions */}
                <div className={`flex flex-col gap-1 mt-4 flex-shrink-0 ${isRTL ? 'items-start' : 'items-end'}`}>
                  <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Button onClick={handleSavePermissions} disabled={loading || !isSaveEnabled()}>
                      {loading && <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />}
                      {isRTL ? 'حفظ' : 'Save'}
                    </Button>
                  </div>
                  {!isSaveEnabled() && getSaveDisabledHint() && (
                    <p className="text-xs text-muted-foreground">
                      {getSaveDisabledHint()}
                    </p>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Signup Settings Tab */}
          <TabsContent value="signup" className="flex-1 overflow-hidden mt-4">
            <div className="space-y-6 p-4 h-full overflow-y-auto">
              <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                <h3 className="text-lg font-medium">
                  {isRTL ? 'إعدادات التسجيل' : 'Signup Settings'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isRTL ? 'التحكم في إمكانية تسجيل المستخدمين الجدد' : 'Control whether new users can sign up'}
                </p>
              </div>

              <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                  <Label htmlFor="global-signup" className="text-sm font-medium">
                    {isRTL ? 'السماح بالتسجيل العام' : 'Allow Public Signup'}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {signupEnabled 
                      ? (isRTL ? 'يمكن للمستخدمين التسجيل من صفحة التسجيل العامة' : 'Users can register through the public signup page')
                      : (isRTL ? 'التسجيل العام معطل - المدراء فقط يمكنهم إنشاء الحسابات' : 'Public signup is disabled - only admins can create accounts')
                    }
                  </p>
                </div>
                <Switch
                  id="global-signup"
                  checked={signupEnabled}
                  onCheckedChange={handleSignupToggle}
                  disabled={signupLoading}
                />
              </div>

              {signupLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isRTL ? 'جاري التحديث...' : 'Updating...'}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Login Methods Tab */}
          <TabsContent value="login" className="flex-1 overflow-hidden mt-4">
            <div className="space-y-6 p-4 h-full overflow-y-auto">
              <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                <h3 className="text-lg font-medium">
                  {isRTL ? 'طرق تسجيل الدخول' : 'Login Methods'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isRTL ? 'التحكم في طرق تسجيل الدخول المتاحة في صفحة الدخول' : 'Control which login methods are available on the login page'}
                </p>
              </div>

              <h4 className="text-sm font-semibold text-primary">{isRTL ? 'الويب' : 'Web'}</h4>
              <div className="space-y-4">
                {/* Phone/WhatsApp OTP */}
                <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone className="h-5 w-5 text-blue-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="phone-otp" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'رمز التحقق عبر الهاتف/واتساب' : 'Phone/WhatsApp OTP'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'السماح بتسجيل الدخول برقم الهاتف ورمز التحقق عبر واتساب' : 'Allow login with phone number and OTP via WhatsApp'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="phone-otp"
                    checked={loginMethods.phoneOtp}
                    onCheckedChange={(checked) => handleLoginMethodToggle('phoneOtp', checked)}
                    disabled={loginMethodsLoading}
                  />
                </div>

                {/* Email/Password */}
                <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Mail className="h-5 w-5 text-green-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="email-password" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'البريد الإلكتروني وكلمة المرور' : 'Email & Password'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'السماح بتسجيل الدخول بالبريد الإلكتروني/الهاتف وكلمة المرور' : 'Allow login with email/phone and password'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="email-password"
                    checked={loginMethods.emailPassword}
                    onCheckedChange={(checked) => handleLoginMethodToggle('emailPassword', checked)}
                    disabled={loginMethodsLoading}
                  />
                </div>

                {/* Google Login */}
                <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Chrome className="h-5 w-5 text-red-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="google-login" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'تسجيل الدخول بـ Google' : 'Google Sign-in'}
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {isRTL ? 'السماح بتسجيل الدخول بحساب Google' : 'Allow sign in with Google account'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id="google-login"
                    checked={loginMethods.googleLogin}
                    onCheckedChange={(checked) => handleLoginMethodToggle('googleLogin', checked)}
                    disabled={loginMethodsLoading}
                  />
                </div>
              </div>

              {loginMethodsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isRTL ? 'جاري الحفظ...' : 'Saving...'}
                </div>
              )}

              {/* Mobile login methods (separate doc: settings/loginMethodsMobile) */}
              <div className={`space-y-1 pt-2 ${isRTL ? 'text-right' : ''}`}>
                <h4 className="text-sm font-semibold text-primary">{isRTL ? 'الجوال (التطبيق)' : 'Mobile (App)'}</h4>
                <p className="text-xs text-muted-foreground">
                  {isRTL ? 'طرق تسجيل الدخول والتسجيل في تطبيق الجوال (لا يتوفر Google في الجوال)' : 'Login & signup methods in the mobile app (Google is not available on mobile)'}
                </p>
              </div>

              <div className="space-y-4">
                {/* Mobile Phone/WhatsApp OTP */}
                <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Phone className="h-5 w-5 text-blue-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="mobile-phone-otp" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'رمز التحقق عبر الهاتف/واتساب' : 'Phone/WhatsApp OTP'}
                      </Label>
                    </div>
                  </div>
                  <Switch
                    id="mobile-phone-otp"
                    checked={mobileLoginMethods.phoneOtp}
                    onCheckedChange={(checked) => handleMobileLoginMethodToggle('phoneOtp', checked)}
                    disabled={mobileLoginMethodsLoading}
                  />
                </div>

                {/* Mobile Email/Password */}
                <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Mail className="h-5 w-5 text-green-600" />
                    <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                      <Label htmlFor="mobile-email-password" className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'البريد الإلكتروني وكلمة المرور' : 'Email & Password'}
                      </Label>
                    </div>
                  </div>
                  <Switch
                    id="mobile-email-password"
                    checked={mobileLoginMethods.emailPassword}
                    onCheckedChange={(checked) => handleMobileLoginMethodToggle('emailPassword', checked)}
                    disabled={mobileLoginMethodsLoading}
                  />
                </div>
              </div>

              {/* Per-platform overrides. A platform's switches start out mirroring the
                  values above; changing one writes an explicit block for that platform
                  only, leaving the other platform on the defaults. */}
              {(['ios', 'android'] as const).map((platform) => (
                <div key={platform} className="space-y-3 pt-2">
                  <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                    <h4 className="text-sm font-semibold text-primary">
                      {platform === 'ios' ? 'iOS' : 'Android'}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {isRTL
                        ? 'تجاوز خاص بهذه المنصة. يلغي الإعدادات أعلاه على هذه المنصة فقط.'
                        : 'Platform-specific override. Applies instead of the settings above on this platform only.'}
                    </p>
                  </div>

                  <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Phone className="h-5 w-5 text-blue-600" />
                      <Label htmlFor={`mobile-${platform}-phone-otp`} className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'رمز التحقق عبر الهاتف/واتساب' : 'Phone/WhatsApp OTP'}
                      </Label>
                    </div>
                    <Switch
                      id={`mobile-${platform}-phone-otp`}
                      checked={mobilePlatformMethods[platform].phoneOtp}
                      onCheckedChange={(checked) => handleMobilePlatformToggle(platform, 'phoneOtp', checked)}
                      disabled={mobileLoginMethodsLoading}
                    />
                  </div>

                  <div className={`flex items-center justify-between p-4 border rounded-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <Mail className="h-5 w-5 text-green-600" />
                      <Label htmlFor={`mobile-${platform}-email-password`} className="text-sm font-medium cursor-pointer">
                        {isRTL ? 'البريد الإلكتروني وكلمة المرور' : 'Email & Password'}
                      </Label>
                    </div>
                    <Switch
                      id={`mobile-${platform}-email-password`}
                      checked={mobilePlatformMethods[platform].emailPassword}
                      onCheckedChange={(checked) => handleMobilePlatformToggle(platform, 'emailPassword', checked)}
                      disabled={mobileLoginMethodsLoading}
                    />
                  </div>
                </div>
              ))}

              {mobileLoginMethodsLoading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {isRTL ? 'جاري الحفظ...' : 'Saving...'}
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>{isRTL ? 'تحذير:' : 'Warning:'}</strong>{' '}
                  {isRTL ? 'يجب تفعيل طريقة تسجيل دخول واحدة على الأقل. التغييرات تسري فوراً.' : 'At least one login method must remain enabled. Changes take effect immediately.'}
                </p>
              </div>

            </div>
          </TabsContent>

          {/* OTP Settings Tab */}
          <TabsContent value="otp" className="flex-1 overflow-hidden mt-4">
            <div className="space-y-6 p-4 h-full overflow-y-auto">
              <div className={`space-y-1 ${isRTL ? 'text-right' : ''}`}>
                <h3 className="text-lg font-medium">
                  {isRTL ? 'طريقة إرسال رمز التحقق (OTP)' : 'OTP Delivery Method'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {isRTL ? 'اختر طريقة إرسال رمز التحقق للمستخدمين: رسالة نصية SMS أو واتساب' : 'Choose how verification codes are sent to users: SMS or WhatsApp'}
                </p>
              </div>

              <div className="space-y-4">
                <div className={`flex flex-wrap gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    otpMethod === 'sms' ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      checked={otpMethod === 'sms'}
                      onChange={() => handleOtpMethodChange('sms')}
                      className="accent-blue-600"
                    />
                    <Smartphone className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium">SMS</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-3 border rounded-lg cursor-pointer transition-colors ${
                    otpMethod === 'whatsapp' ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:bg-gray-50'
                  }`}>
                    <input
                      type="radio"
                      checked={otpMethod === 'whatsapp'}
                      onChange={() => handleOtpMethodChange('whatsapp')}
                      className="accent-green-600"
                    />
                    <MessageSquare className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium">WhatsApp</span>
                  </label>
                </div>

                {otpMethod === 'sms' && providerStatus && !providerStatus.authentica.configured && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className={`text-xs text-amber-800 ${isRTL ? 'text-right' : ''}`}>
                      <strong>{isRTL ? 'تنبيه:' : 'Note:'}</strong>{' '}
                      {isRTL 
                        ? 'إرسال SMS يتطلب مفتاح Authentica API. بدونه سيتم إرسال الرمز عبر واتساب تلقائياً.'
                        : 'SMS delivery requires an Authentica API key. Without it, OTP will be sent via WhatsApp automatically.'}
                    </p>
                  </div>
                )}

                <div className="space-y-2" data-testid="authentica-key-status">
                  <Label className={`text-sm font-medium ${isRTL ? 'text-right block' : ''}`}>
                    {isRTL ? 'مفتاح Authentica API' : 'Authentica API Key'}
                  </Label>
                  <p className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                    {keyStatusText(providerStatus?.authentica, isRTL)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isRTL
                      ? 'مطلوب لإرسال OTP عبر SMS. بدونه سيتم استخدام واتساب فقط. لا يُعرض المفتاح ولا يُعدَّل هنا.'
                      : 'Required for SMS delivery. Without it, WhatsApp (Evolution API) is used as fallback. The key is not shown or edited here.'}
                  </p>
                </div>
              </div>

              <div className="border-t pt-6">
                <div className={`space-y-1 mb-4 ${isRTL ? 'text-right' : ''}`}>
                  <h3 className="text-lg font-medium">
                    {isRTL ? 'إعدادات Evolution API (واتساب)' : 'Evolution API Settings (WhatsApp)'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isRTL 
                      ? 'إعدادات الاتصال بخدمة Evolution API لإرسال رسائل واتساب كبديل عند عدم توفر Authentica'
                      : 'Evolution API connection settings for WhatsApp messaging fallback when Authentica is not available'}
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="evolution-api-url" className={`text-sm font-medium ${isRTL ? 'text-right block' : ''}`}>
                      {isRTL ? 'رابط API' : 'API URL'}
                    </Label>
                    <Input
                      id="evolution-api-url"
                      type="url"
                      value={evolutionApiUrl}
                      onChange={(e) => setEvolutionApiUrl(e.target.value)}
                      placeholder="https://evo.example.com"
                      dir="ltr"
                      className="text-left font-mono text-sm"
                    />
                  </div>

                  <div className="space-y-2" data-testid="evolution-key-status">
                    <Label className={`text-sm font-medium ${isRTL ? 'text-right block' : ''}`}>
                      {isRTL ? 'مفتاح API' : 'API Key'}
                    </Label>
                    <p className={`text-sm ${isRTL ? 'text-right' : ''}`}>
                      {keyStatusText(providerStatus?.evolution, isRTL)}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="evolution-instance-name" className={`text-sm font-medium ${isRTL ? 'text-right block' : ''}`}>
                      {isRTL ? 'اسم المثيل (Instance)' : 'Instance Name'}
                    </Label>
                    <Input
                      id="evolution-instance-name"
                      value={evolutionInstanceName}
                      onChange={(e) => setEvolutionInstanceName(e.target.value)}
                      placeholder="namatbot"
                      dir="ltr"
                      className="text-left font-mono text-sm"
                    />
                  </div>

                  <Button
                    onClick={handleSaveEvolutionSettings}
                    disabled={evolutionLoading}
                    size="sm"
                  >
                    {evolutionLoading && <Loader2 className={`h-4 w-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />}
                    {isRTL ? 'حفظ إعدادات Evolution API' : 'Save Evolution API Settings'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className={`flex gap-2 pt-4 border-t flex-shrink-0 ${isRTL ? 'justify-start flex-row-reverse' : 'justify-end'}`}>
          <DialogClose asChild>
            <Button variant="outline">
              {isRTL ? 'إغلاق' : 'Close'}
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
